/**
 * Workflow Step Component
 * Displays a single step in the workflow timeline with status, progress, and actions
 */

import {
  CheckCircle,
  Circle,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Image as ImageIcon,
  Clock,
  Package,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  StepStatus,
  WorkflowStepId,
  TemplateWorkflowStepId,
  StepAction,
  TemplateSelectionStatus,
  PlanningStatus,
  SlideGenerationStatus,
  VideoGenerationStatus,
} from '@/types/workflow';
import { PlanningProgress } from './PlanningProgress';
import { TemplateSelectionProgress } from './TemplateSelectionProgress';
import { VideoGenerationProgress } from './VideoGenerationProgress';

// Generic action type that supports both workflow action styles
export interface GenericStepAction {
  id: string;
  label: string;
  icon?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  isPrimary?: boolean;
  isEnabled?: (status: StepStatus) => boolean;
  handler?: (conversationId: string, stepId: string) => Promise<void>;
  onClick?: () => void | Promise<void>;
}

interface WorkflowStepProps {
  stepId: WorkflowStepId | TemplateWorkflowStepId | string;
  title: string;
  description: string;
  status: StepStatus;
  stepNumber: number;
  isCurrentStep?: boolean;
  isLastStep?: boolean;
  isExpanded?: boolean;
  showConnector?: boolean;
  actions?: (StepAction | GenericStepAction)[];
  conversationId: string;
  onToggleExpand?: () => void;
  className?: string;
}

// Get icon component based on step ID
const getStepIcon = (stepId: string, status: string) => {
  const iconClass = 'h-5 w-5';

  if (status === 'in_progress') {
    return <Loader2 className={cn(iconClass, 'animate-spin')} />;
  }

  if (status === 'completed') {
    return <CheckCircle className={cn(iconClass, 'text-green-600')} />;
  }

  if (status === 'error') {
    return <AlertCircle className={cn(iconClass, 'text-red-600')} />;
  }

  // Pending state - show step-specific icon
  switch (stepId) {
    case 'template_selection':
      return <Package className={cn(iconClass, 'text-gray-400')} />;
    case 'planning':
      return <Lightbulb className={cn(iconClass, 'text-gray-400')} />;
    case 'slide_generation':
    case 'layout_generation':
      return <ImageIcon className={cn(iconClass, 'text-gray-400')} />;
    case 'validation':
      return <CheckCircle className={cn(iconClass, 'text-gray-400')} />;
    case 'design_document':
      return <Package className={cn(iconClass, 'text-gray-400')} />;
    default:
      return <Circle className={cn(iconClass, 'text-gray-400')} />;
  }
};

// Get status badge color
const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'in_progress':
      return 'bg-accent text-accent-foreground border-primary/30';
    case 'completed':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'error':
      return 'bg-red-100 text-red-700 border-red-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

// Get background color for step card
const getStepBackgroundColor = (status: string, isCurrentStep?: boolean) => {
  if (status === 'error') return 'bg-red-50 border-red-200';
  if (status === 'completed') return 'bg-green-50 border-green-200';
  if (status === 'in_progress' || isCurrentStep) return 'bg-accent border-primary/30';
  return 'bg-gray-50 border-gray-200';
};

// Format duration
const formatDuration = (startedAt?: Date | string, completedAt?: Date | string) => {
  if (!startedAt) return null;

  // Convert to Date objects if they're strings (from localStorage)
  const start = typeof startedAt === 'string' ? new Date(startedAt) : startedAt;
  const end = completedAt
    ? (typeof completedAt === 'string' ? new Date(completedAt) : completedAt)
    : new Date();

  const duration = Math.floor((end.getTime() - start.getTime()) / 1000);

  if (duration < 60) return `${duration}s`;
  const mins = Math.floor(duration / 60);
  const secs = duration % 60;
  return `${mins}m ${secs}s`;
};

export function WorkflowStep({
  stepId,
  title,
  description,
  status,
  stepNumber,
  isCurrentStep,
  isLastStep,
  isExpanded: isExpandedProp,
  // showConnector is currently unused, kept for future implementation
  showConnector: _showConnector,
  actions = [],
  conversationId,
  onToggleExpand,
  className,
}: WorkflowStepProps) {
  // Use prop if provided, otherwise fallback to status.isExpanded
  const isExpanded = isExpandedProp !== undefined ? isExpandedProp : (status.isExpanded || false);

  // Cast to specific status types for detailed rendering
  const templateStatus = stepId === 'template_selection' ? (status as TemplateSelectionStatus) : null;
  const planningStatus = stepId === 'planning' ? (status as PlanningStatus) : null;
  const slideGenStatus = stepId === 'slide_generation' ? (status as SlideGenerationStatus) : null;
  const videoGenStatus = stepId === 'video_generation' ? (status as VideoGenerationStatus) : null;

  // Check if step has expandable details (slide_generation doesn't need details section anymore)
  const hasDetails = status.subTasks && status.subTasks.length > 0;

  // Debug logging for slide generation step
  if (stepId === 'slide_generation' && slideGenStatus) {
    console.log('🎬 [WorkflowStep] Rendering slide_generation step:', {
      status: slideGenStatus.status,
      totalSlides: slideGenStatus.totalSlides,
      completedSlides: slideGenStatus.completedSlides,
      hasSlides: !!slideGenStatus.slides,
      slidesLength: slideGenStatus.slides?.length || 0,
      isExpanded
    });
  }

  const duration = formatDuration(status.startedAt, status.completedAt);

  // Get progress text for collapsed view
  const getProgressText = () => {
    if (stepId === 'slide_generation' && slideGenStatus) {
      const completed = slideGenStatus.completedSlides || 0;
      const total = slideGenStatus.totalSlides || 0;
      if (total > 0) {
        return `${completed}/${total} slides`;
      }
    }
    if (stepId === 'planning' && planningStatus?.sceneCount !== undefined && status.status === 'completed') {
      return `${planningStatus.sceneCount} scenes`;
    }
    if (stepId === 'template_selection' && templateStatus?.selectedTemplateName) {
      return templateStatus.selectedTemplateName;
    }
    return null;
  };

  const progressText = getProgressText();

  // Collapsed view - single line
  if (!isExpanded) {
    return (
      <div className={cn('relative', className)}>
        {/* Connecting line to next step */}
        {!isLastStep && (
          <div className="absolute left-[18px] top-[28px] w-[2px] h-[calc(100%+8px)] bg-gray-200" />
        )}

        {/* Compact Step Row */}
        <button
          onClick={onToggleExpand}
          className={cn(
            'relative w-full flex items-center gap-3 p-2 rounded-lg border transition-all duration-200 overflow-hidden',
            'hover:shadow-sm hover:border-gray-300',
            status.status === 'completed' && 'border-green-200 bg-green-50/30',
            status.status === 'in_progress' && 'border-primary/30 bg-accent/30',
            status.status === 'error' && 'border-red-200 bg-red-50/30',
            status.status === 'pending' && 'border-gray-200 bg-gray-50/30',
            isCurrentStep && 'ring-2 ring-primary/30'
          )}
        >
          {/* Gradient Progress Fill (Collapsed) */}
          {/* Template Selection - Green when completed, grey when pending */}
          {stepId === 'template_selection' && status.status === 'completed' && (
            <div
              className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
              style={{
                background: `rgba(74, 222, 128, 0.25)`,
              }}
            />
          )}

          {/* Video Planning - Green when completed, grey when pending */}
          {stepId === 'planning' && status.status === 'completed' && (
            <div
              className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
              style={{
                background: `rgba(74, 222, 128, 0.25)`,
              }}
            />
          )}

          {/* Scene Generation - Progressive green gradient based on slides completed */}
          {stepId === 'slide_generation' && slideGenStatus && slideGenStatus.totalSlides && slideGenStatus.totalSlides > 0 && (
            <div
              className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
              style={{
                background: `linear-gradient(to right, rgba(74, 222, 128, 0.25) 0%, rgba(74, 222, 128, 0.25) ${
                  ((slideGenStatus.completedSlides || 0) / (slideGenStatus.totalSlides || 1)) * 100
                }%, transparent ${((slideGenStatus.completedSlides || 0) / (slideGenStatus.totalSlides || 1)) * 100}%)`,
              }}
            />
          )}

          {/* Audio Generation - Green when completed */}
          {stepId === 'audio_generation' && status.status === 'completed' && (
            <div
              className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
              style={{
                background: `rgba(74, 222, 128, 0.25)`,
              }}
            />
          )}

          {/* Video Generation - Green when completed */}
          {stepId === 'video_generation' && status.status === 'completed' && (
            <div
              className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
              style={{
                background: `rgba(74, 222, 128, 0.25)`,
              }}
            />
          )}
          {/* Status Icon - Smaller */}
          <div
            className={cn(
              'relative z-10 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-all',
              status.status === 'completed' && 'bg-green-100 border-green-400',
              status.status === 'in_progress' && 'bg-accent border-primary',
              status.status === 'error' && 'bg-red-100 border-red-400',
              status.status === 'pending' && 'bg-gray-100 border-gray-300'
            )}
          >
            {getStepIcon(stepId, status.status)}
          </div>

          {/* Step Info */}
          <div className="relative z-10 flex-1 min-w-0 text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">
                {stepNumber}. {title}
              </span>
              {status.status === 'in_progress' && (
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                </div>
              )}
            </div>
          </div>

          {/* Progress Text */}
          {progressText && (
            <div className="relative z-10 flex-shrink-0 text-xs text-gray-600 font-medium">
              {progressText}
            </div>
          )}

          {/* Status Badge - Compact */}
          <div className="relative z-10 flex-shrink-0">
            <Badge
              variant="outline"
              className={cn('text-xs h-5 px-2', getStatusBadgeColor(status.status))}
            >
              {status.status === 'completed' && '✓'}
              {status.status === 'in_progress' && '⏳'}
              {status.status === 'error' && '✗'}
              {status.status === 'pending' && '○'}
            </Badge>
          </div>

          {/* Expand Chevron */}
          <ChevronRight className="relative z-10 h-4 w-4 text-gray-400 flex-shrink-0" />
        </button>
      </div>
    );
  }

  // Expanded view - full details (existing implementation)
  return (
    <div className={cn('relative', className)}>
      {/* Connecting line to next step */}
      {!isLastStep && (
        <div className="absolute left-[20px] top-[50px] w-[2px] h-[calc(100%+16px)] bg-gradient-to-b from-gray-300 to-gray-200" />
      )}

      {/* Step Card */}
      <div
        className={cn(
          'relative rounded-lg border-2 transition-all duration-300 overflow-hidden',
          getStepBackgroundColor(status.status, isCurrentStep),
          isCurrentStep && 'shadow-md',
          className
        )}
      >
        {/* Gradient Progress Fill (Expanded) */}
        {/* Template Selection - Green when completed, grey when pending */}
        {stepId === 'template_selection' && status.status === 'completed' && (
          <div
            className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
            style={{
              background: `rgba(74, 222, 128, 0.25)`,
            }}
          />
        )}

        {/* Video Planning - Green when completed, grey when pending */}
        {stepId === 'planning' && status.status === 'completed' && (
          <div
            className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
            style={{
              background: `rgba(74, 222, 128, 0.25)`,
            }}
          />
        )}

        {/* Scene Generation - Progressive green gradient based on slides completed */}
        {stepId === 'slide_generation' && slideGenStatus && slideGenStatus.totalSlides && slideGenStatus.totalSlides > 0 && (
          <div
            className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
            style={{
              background: `linear-gradient(to right, rgba(74, 222, 128, 0.25) 0%, rgba(74, 222, 128, 0.25) ${
                ((slideGenStatus.completedSlides || 0) / (slideGenStatus.totalSlides || 1)) * 100
              }%, transparent ${((slideGenStatus.completedSlides || 0) / (slideGenStatus.totalSlides || 1)) * 100}%)`,
            }}
          />
        )}

        {/* Audio Generation - Green when completed */}
        {stepId === 'audio_generation' && status.status === 'completed' && (
          <div
            className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
            style={{
              background: `rgba(74, 222, 128, 0.25)`,
            }}
          />
        )}

        {/* Video Generation - Green when completed */}
        {stepId === 'video_generation' && status.status === 'completed' && (
          <div
            className="absolute inset-0 transition-all duration-700 ease-out pointer-events-none"
            style={{
              background: `rgba(74, 222, 128, 0.25)`,
            }}
          />
        )}

        <div className="p-4 relative z-10">
          {/* Header */}
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div
              className={cn(
                'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all',
                status.status === 'completed'
                  ? 'bg-green-100 border-green-300'
                  : status.status === 'in_progress'
                  ? 'bg-accent border-primary/30'
                  : status.status === 'error'
                  ? 'bg-red-100 border-red-300'
                  : 'bg-gray-100 border-gray-300'
              )}
            >
              {getStepIcon(stepId, status.status)}
            </div>

            {/* Title and Description */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {stepNumber}. {title}
                  </h3>
                  <Badge variant="outline" className={cn('text-xs', getStatusBadgeColor(status.status))}>
                    {status.status === 'in_progress' ? 'In Progress' : status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                  </Badge>
                </div>
                {/* Collapse button */}
                <button
                  onClick={onToggleExpand}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>

              {/* Description */}
              {!(stepId === 'planning' && planningStatus?.substeps) && (
                <p className="text-xs text-gray-600 mb-2">{description}</p>
              )}

              {/* Duration */}
              {duration && !(stepId === 'planning' && planningStatus?.substeps) && stepId !== 'template_selection' && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                  <Clock className="h-3 w-3" />
                  <span>{duration}</span>
                </div>
              )}

              {/* Progress Bar - Gradient for Slide Generation */}
              {status.status === 'in_progress' && status.progress !== undefined && !planningStatus?.substeps && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Progress</span>
                    <span className="text-xs font-medium text-accent-foreground">{Math.round(status.progress)}%</span>
                  </div>
                  {stepId === 'slide_generation' && slideGenStatus ? (
                    // Gradient progress bar for slide generation
                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full transition-all duration-500 ease-out bg-primary"
                        style={{ width: `${status.progress}%` }}
                      />
                    </div>
                  ) : (
                    // Standard progress bar for other steps
                    <Progress value={status.progress} className="h-2" />
                  )}
                </div>
              )}

              {/* Template Selection Progress */}
              {stepId === 'template_selection' && templateStatus && (
                <div className="mb-3">
                  <TemplateSelectionProgress templateStatus={templateStatus} />
                </div>
              )}

              {/* Planning Progress (Deterministic 3-step workflow) */}
              {stepId === 'planning' && status.status === 'in_progress' && planningStatus?.substeps && (
                <div className="mb-3">
                  <PlanningProgress planningStatus={planningStatus} />
                </div>
              )}

              {/* Video Generation Progress (Deterministic 3-step workflow) */}
              {stepId === 'video_generation' && status.status === 'in_progress' && videoGenStatus?.substeps && (
                <div className="mb-3">
                  <VideoGenerationProgress videoGenerationStatus={videoGenStatus} />
                </div>
              )}

              {/* Error Message */}
              {status.status === 'error' && status.error && (
                <div className="flex items-start gap-2 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-700 mb-2">
                  <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                  <span>{status.error}</span>
                </div>
              )}

              {/* Action Buttons */}
              {actions.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {actions.map((action) => {
                    const isEnabled = action.isEnabled ? action.isEnabled(status) : true;
                    const handleClick = () => {
                      if ('handler' in action && action.handler) {
                        // Cast stepId to appropriate type for handler
                        return action.handler(conversationId, stepId as WorkflowStepId);
                      } else if ('onClick' in action && action.onClick) {
                        return action.onClick();
                      }
                    };

                    return (
                      <Button
                        key={action.id}
                        size="sm"
                        variant={action.variant || (action.isPrimary ? 'default' : 'outline')}
                        disabled={!isEnabled}
                        onClick={handleClick}
                        className="text-xs h-7"
                      >
                        {action.label}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Expanded Details */}
          {hasDetails && (
            <div className="mt-3 pl-13 space-y-3 animate-in slide-in-from-top-2 duration-200">
              {/* Sub-tasks */}
              {status.subTasks && status.subTasks.length > 0 && (
                <div className="bg-white rounded-lg p-3 border">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Tasks</h4>
                  <div className="space-y-2">
                    {status.subTasks.map((task) => (
                      <div key={task.id} className="flex items-center gap-2 text-xs">
                        <div className="flex-shrink-0">
                          {task.status === 'completed' ? (
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          ) : task.status === 'in_progress' ? (
                            <Loader2 className="h-3 w-3 text-primary animate-spin" />
                          ) : task.status === 'error' ? (
                            <AlertCircle className="h-3 w-3 text-red-600" />
                          ) : (
                            <Circle className="h-3 w-3 text-gray-400" />
                          )}
                        </div>
                        <span className="flex-1 text-gray-700">{task.title}</span>
                        {task.progress !== undefined && (
                          <span className="text-gray-500">{Math.round(task.progress)}%</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Planning Details */}
              {planningStatus?.sceneCount !== undefined && (
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-700">Scene Count:</span>
                    <span className="font-medium">{planningStatus.sceneCount}</span>
                  </div>
                </div>
              )}

              {/* Slide Generation Details - Hidden per user request */}
              {/* SlidePickerGrid component removed - user prefers View Gallery button only */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
